import { Box } from '../joy/Box';
import { IconButton } from '../joy/IconButton';
import { Tooltip } from '../joy/Tooltip';
import { ButtonGroup } from '../joy/ButtonGroup';
import { Trans } from '@lingui/react';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Fragment, useState } from 'react';
import { BaseItem, Column, ItemListProps } from './types';
import { useDnd } from './useDnd';
import { Link } from 'react-router-dom';
import { styled } from '@mui/joy/styles';
import { isFunction } from 'lodash-es';

const StyledLink = styled(Link)({ display: 'table-row' });
const StyledTr = styled('tr')();

interface RowProps<T extends BaseItem> {
  item: T;
  columns: Column<T>[];
  columnCount: number;
  actions?: ItemListProps<T>['actions'];
  onDelete?: ItemListProps<T>['onDelete'];
  onItemClick?: ItemListProps<T>['onItemClick'];
  linkTo?: ItemListProps<T>['linkTo'];
  onDrop?: ItemListProps<T>['onDrop'];
  extraRow?: ItemListProps<T>['extraRow'];
}

export const Row = <T extends BaseItem>({
  item,
  columns,
  onDelete,
  onItemClick,
  linkTo,
  onDrop,
  actions,
  extraRow,
  columnCount,
}: RowProps<T>) => {
  const { isDragging, aboutToBeDropped, canBeDropped, ref } = useDnd(item, onDrop);
  const [open, setOpen] = useState(false);
  const to = linkTo?.(item);

  const Component = to ? StyledLink : StyledTr;

  return (
    <Fragment key={item.id}>
      <Component
        to={to ? to : ''}
        onClick={() => onItemClick?.(item)}
        sx={theme => ({
          opacity: isDragging ? 0.4 : 1,
          backgroundColor: aboutToBeDropped
            ? theme.palette.success.plainActiveBg
            : canBeDropped
              ? theme.palette.success.outlinedActiveBg
              : undefined,
          '&:hover': {
            backgroundColor: theme.palette.neutral.plainHoverBg,
          },
          '& .actions': {
            visibility: 'hidden',
          },
          '&:hover .actions': {
            visibility: 'visible',
          },
        })}
        // @ts-expect-error all
        ref={ref}
      >
        {extraRow?.toggle && (
          <td>
            <IconButton variant="outlined" onClick={() => setOpen(!open)}>
              {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </td>
        )}
        {columns.map((column, index) => (
          <td key={index} align={column.align}>
            {column.render(item) ?? ''}
          </td>
        ))}

        {(actions || item.deletable) && (
          <td style={{ textAlign: 'right' }}>
            <ButtonGroup sx={{ justifyContent: 'flex-end', position: 'relative', zIndex: 0 }} className="actions">
              {actions?.(item).map((action, index) => (
                <Tooltip title={action.label} key={index}>
                  <IconButton
                    variant="outlined"
                    onClick={
                      action.onClick
                        ? (e: React.MouseEvent) => {
                            e.stopPropagation();
                            e.preventDefault();
                            return action.onClick?.(item);
                          }
                        : undefined
                    }
                    component={action.link ? Link : 'button'}
                    to={isFunction(action.link) ? action.link?.(item) : action.link}
                  >
                    {action.icon}
                  </IconButton>
                </Tooltip>
              ))}
              {item.deletable && (
                <Tooltip title={<Trans id="Delete" />}>
                  <IconButton
                    color="danger"
                    variant="outlined"
                    onClick={e => {
                      e.stopPropagation();
                      e.preventDefault();
                      onDelete?.(item);
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              )}
            </ButtonGroup>
          </td>
        )}
      </Component>
      {open && extraRow ? (
        extraRow.fullWidth ? (
          <Box
            component="tr"
            sx={{
              '&': { backgroundColor: 'transparent !important' /** override hoverRow styling from parent table. */ },
            }}
          >
            <td colSpan={columnCount}>{extraRow.render(item)}</td>
          </Box>
        ) : (
          extraRow.render(item)
        )
      ) : null}
    </Fragment>
  );
};
